import { useMemo } from 'react';
import { useGetLotAnimalEbvsQuery } from '@/publicSearch/graphql/queries.generated';
import { buildAnimalLotEbvsQueryOptions } from './animalLotEbvsQuery';

type AnimalSaleEntry = {
  saleId: number;
  saleName: string;
  saleType: string;
  saleDate?: string | null;
  publishTillDate?: string | null;
  lotNo: string;
  lotNotes?: string | null;
  lotAnimalId?: number | null;
};

type AnimalSaleHistoryParams = {
  animalId?: string | number | null;
  orgId?: string | number | null;
};

export function useAnimalSaleHistory({
  animalId,
  orgId,
}: AnimalSaleHistoryParams) {
  const enabled = Boolean(animalId && orgId);

  const { data, loading, error } = useGetLotAnimalEbvsQuery(
    buildAnimalLotEbvsQueryOptions({ animalId, orgId, enabled }),
  );

  const entries = useMemo<AnimalSaleEntry[]>(() => {
    const items = data?.lotAnimalEbvs?.items ?? [];
    return items.map((item) => ({
      saleId: item.saleId,
      saleName: item.sale?.saleName ?? 'Unknown sale',
      saleType: item.sale?.saleType ?? '',
      saleDate: item.sale?.saleDate ? String(item.sale?.saleDate) : null,
      publishTillDate: item.sale?.publishTillDate
        ? String(item.sale?.publishTillDate)
        : null,
      lotNo: item.lotNo,
      lotNotes: item.lotNotes ?? null,
      lotAnimalId: item.lotAnimalId ?? null,
    }));
  }, [data?.lotAnimalEbvs?.items]);

  const { activeSales, previousSales, activeLotNotes } = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const yesterday = new Date(todayStart);
    yesterday.setDate(yesterday.getDate() - 1);

    const active: AnimalSaleEntry[] = [];
    const previous: AnimalSaleEntry[] = [];

    entries.forEach((entry) => {
      const saleDate = entry.saleDate ? new Date(entry.saleDate) : null;
      const publishTillDate = entry.publishTillDate
        ? new Date(entry.publishTillDate)
        : null;
      const hasValidSaleDate = saleDate && !Number.isNaN(saleDate.getTime());
      const hasValidPublishTill =
        publishTillDate && !Number.isNaN(publishTillDate.getTime());

      if (!hasValidSaleDate || !hasValidPublishTill) return;
      if (publishTillDate < todayStart) return;

      if (saleDate >= todayStart) {
        active.push(entry);
      } else if (saleDate < todayStart) {
        previous.push(entry);
      }
    });

    return {
      activeSales: active,
      previousSales: previous,
      activeLotNotes: active[0]?.lotNotes ?? null,
    };
  }, [entries]);

  return {
    activeSales,
    previousSales,
    activeLotNotes,
    loading,
    error,
  };
}
